import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, Menu, X } from 'lucide-react';
import { NavigationItem } from '../types';
import './MobileMenuOverlay.css';

const Navigation: React.FC = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState<boolean>(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [hoverTimeout, setHoverTimeout] = useState<NodeJS.Timeout | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  const navigationItems: NavigationItem[] = [
    {
      title: "Specialty Services",
      path: "/services",
      items: [
        {
          name: "Chronic Pain & Dysfunction",
          path: "/services/chronic-pain",
          description: "Root-cause approach for lasting relief"
        },
        {
          name: "Sports & Orthopedic Injuries",
          path: "/services/sports-injuries",
          description: "Elite-level care for athletes and active individuals"
        },
        {
          name: "Post-Surgical Rehabilitation",
          path: "/services/post-surgical",
          description: "Comprehensive recovery programs"
        },
        {
          name: "Headaches & TMJ Dysfunction",
          path: "/services/headaches-tmj",
          description: "Advanced treatment for head, neck, and jaw pain"
        },
        {
          name: "Vestibular & Balance Disorders",
          path: "/services/vestibular-balance",
          description: "Specialized care for dizziness and vertigo"
        },
        {
          name: "Work Injuries & Ergonomics",
          path: "/services/work-injuries",
          description: "Treatment and prevention of workplace injuries"
        },
        {
          name: "Auto Accident Injuries",
          path: "/services/auto-accident-injuries",
          description: "Car accident recovery with insurance support"
        },
        {
          name: "Movement Impairment Syndromes",
          path: "/services/movement-impairment",
          description: "Correcting faulty movement patterns"
        },
        {
          name: "Neuromuscular Disorders",
          path: "/services/neuromuscular-disorders",
          description: "Specialized care for neurological conditions"
        },
        {
          name: "Pediatric Physical Therapy",
          path: "/services/pediatric-therapy",
          description: "Specialized care for children"
        }
      ],
    },
    {
      title: "Advanced Technology",
      path: "/technology",
      items: [
        {
          name: "Technology Overview",
          path: "/technology",
          description: "Precision tools powering our holistic approach"
        },
        {
          name: "Moti Physio",
          path: "/technology/moti-physio",
          description: "AI-powered markerless 3D posture analysis"
        },
        {
          name: "Baiobit",
          path: "/technology/baiobit",
          description: "Wearable motion sensor for objective movement analysis"
        },
        {
          name: "Vestibular First Goggles",
          path: "/technology/vestibular-goggles",
          description: "Infrared VNG goggles for precise BPPV & vestibular diagnosis"
        }
      ],
    },
    {
      title: "About",
      path: "/about",
      items: [
        {
          name: "About the Practice",
          path: "/about",
          description: "Meet Kumaraswamy and our approach to care"
        },
        {
          name: "Blog & Articles",
          path: "/blog",
          description: "Expert insights, tips, and patient education"
        },
        {
          name: "Resource Center",
          path: "/resource-center",
          description: "Educational videos and treatment information"
        },
        {
          name: "FAQ",
          path: "/faq",
          description: "Answers to common questions about our care"
        }
      ],
    },
    {
      title: "Clinic",
      path: "/location",
      items: [
        {
          name: "Location & Hours",
          path: "/location",
          description: "Find us and our operating hours"
        },
        {
          name: "Direct Access",
          path: "/direct-access",
          description: "Schedule without a physician referral"
        },
        {
          name: "Contact Us",
          path: "/contact",
          description: "Get in touch with our team"
        },
        {
          name: "Careers",
          path: "/careers",
          description: "Join our team at HMPT"
        }
      ],
    },
    { title: "Provider", path: "/provider", items: [] },
  ];


  const handleRequestAppointment = (): void => {
    window.open(
      "https://holisticmanualptllc.patient.sprypt.com/create-appointments?clinic=460&therapist=392849&type=2683",
      "_blank"
    );
  };

  const handleMouseEnter = (itemTitle: string): void => {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
    setActiveDropdown(itemTitle);
  };

  const handleMouseLeave = (): void => {
    const timeout = setTimeout(() => {
      setActiveDropdown(null);
    }, 150);
    setHoverTimeout(timeout);
  };

  const handleDropdownMouseEnter = (): void => {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
  };

  const handleDropdownMouseLeave = (): void => {
    const timeout = setTimeout(() => {
      setActiveDropdown(null);
    }, 150);
    setHoverTimeout(timeout);
  };

  return (
    <nav className="bg-white shadow-sm sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20 mobile-nav-header">
          {/* Logo - responsive sizing for mobile landscape */}
          <Link to="/" className="flex items-center">
            <div className="w-16 h-16 mr-4">
              <img
                src="/HMPT Logo.png"
                alt="Holistic Manual Physical Therapy"
                className="w-16 h-16 mobile-nav-logo object-contain hover:scale-105 transition-transform duration-200"
              />
            </div>
            <div>
              <div className="text-slate-900 text-2xl font-bold mobile-nav-logo-text">Holistic Manual</div>
              <div className="text-slate-700 text-sm mobile-nav-logo-subtext">Physical Therapy</div>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            {navigationItems.map((item) => (
              <div
                key={item.title}
                className="relative"
                onMouseEnter={() => 
                  item.items && item.items.length > 0 
                    ? handleMouseEnter(item.title) 
                    : null
                }
                onMouseLeave={() => 
                  item.items && item.items.length > 0 
                    ? handleMouseLeave() 
                    : null
                }
              >
                {item.items.length > 0 ? (
                  <button 
                    className="flex items-center text-slate-700 hover:text-slate-900 px-4 py-6 text-sm font-semibold transition-colors duration-200"
                    onClick={() => navigate(item.path)}
                  >
                    {item.title}
                    <ChevronDown 
                      className={`ml-1 w-4 h-4 transition-transform duration-200 ${
                        activeDropdown === item.title ? "rotate-180" : ""
                      }`} 
                    />
                  </button>
                ) : (
                  <Link
                    to={item.path}
                    className={`flex items-center px-4 py-6 text-sm font-semibold transition-colors duration-200 ${
                      location.pathname === item.path
                        ? "text-slate-900 border-b-2 border-amber-400"
                        : "text-slate-700 hover:text-slate-900"
                    }`}
                  >
                    {item.title}
                  </Link>
                )}

                {/* Responsive dropdown menu */}
                {activeDropdown === item.title && item.items.length > 0 && (
                  <div 
                    className="absolute top-full left-1/2 transform -translate-x-1/2 bg-white shadow-xl border border-slate-200 rounded-lg z-40 transition-all duration-200 ease-in-out w-[600px] md:w-[700px] lg:w-[800px] max-w-[90vw]"
                    onMouseEnter={handleDropdownMouseEnter}
                    onMouseLeave={handleDropdownMouseLeave}
                  >
                    <div className="p-4 md:p-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
                        {item.items.map((subItem) => (
                          <Link
                            key={subItem.name}
                            to={subItem.path}
                            className="p-3 md:p-4 rounded-lg hover:bg-slate-50 cursor-pointer border border-slate-100 transition-all duration-200 hover:shadow-md hover:border-amber-200 block group"
                            onClick={() => setActiveDropdown(null)}
                          >
                            <div className="font-semibold text-slate-900 mb-2 text-sm group-hover:text-amber-600 transition-colors">
                              {subItem.name}
                            </div>
                            {subItem.description && (
                              <div className="text-xs text-slate-600 leading-relaxed">
                                {subItem.description}
                              </div>
                            )}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden md:flex items-center space-x-4">
            <button
              className="bg-slate-900 text-white px-6 py-3 rounded-lg font-semibold hover:bg-slate-800 transition-all duration-300 transform hover:scale-105 hover:shadow-2xl hover:shadow-slate-900/40"
              onClick={handleRequestAppointment}
            >
              Request Appointment
            </button>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-slate-700 p-2"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation - Full Screen Overlay */}
      {mobileMenuOpen && (
        <div className="md:hidden mobile-menu-overlay">
          {/* Mobile menu header */}
          <div className="mobile-menu-header flex items-center justify-between">
            <div className="flex items-center">
              <div className="w-10 h-10 mr-3">
                <img
                  src="/HMPT Logo.png"
                  alt="Holistic Manual Physical Therapy"
                  className="w-10 h-10 object-contain"
                />
              </div>
              <div>
                <div className="text-slate-900 text-lg font-bold">Menu</div>
              </div>
            </div>
            <button
              onClick={() => setMobileMenuOpen(false)}
              className="p-2 text-slate-700 hover:text-slate-900 mobile-menu-touch-target"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Mobile menu content */}
          <div className="mobile-menu-content">
            <div>
              {navigationItems.map((item) => (
                <div key={item.title} className="mobile-menu-item">
                  <Link
                    to={item.path}
                    className="mobile-menu-main-link mobile-menu-link text-slate-900 hover:text-amber-600 transition-colors mobile-menu-touch-target"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item.title}
                  </Link>
                  {item.items.length > 0 && (
                    <div className="mobile-menu-sub-section">
                      {item.items.map((subItem) => (
                        <div key={subItem.name} className="mobile-menu-sub-item">
                          <Link
                            to={subItem.path}
                            className="mobile-menu-sub-link mobile-menu-link text-slate-700 hover:text-slate-900 transition-colors mobile-menu-touch-target"
                            onClick={() => setMobileMenuOpen(false)}
                          >
                            {subItem.name}
                          </Link>
                          {subItem.description && (
                            <p className="mobile-menu-description text-slate-500">
                              {subItem.description}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* CTA Button - Fixed at bottom */}
          <div className="mobile-menu-footer">
            <button
              className="w-full bg-slate-900 text-white rounded-lg font-semibold hover:bg-slate-800 transition-all duration-300 transform hover:scale-105 hover:shadow-2xl hover:shadow-slate-900/40 mobile-menu-cta-button mobile-menu-touch-target"
              onClick={() => {
                handleRequestAppointment();
                setMobileMenuOpen(false);
              }}
            >
              Request Appointment
            </button>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navigation;